import * as React from 'react';

import Select, {
	SelectValue,
} from '../../../../BaseModule/Components/Select/Select';
import UserPickerAdd from './Add/UserPickerAdd';
import UserPickerItem, { UserItem } from './UserPickerItem/UserPickerItem';
import netteApiClient from '../../../../../Services/NetteApiClient';
import { useMemo, useState } from 'react';
import { useEffectAsync } from '../../../../../helpers';
import { useLazyFamilyMembersAvailableForCaseCreateQuery } from '../../../../../graphql/graphql';
import Loading from '../../../../BaseModule/Components/Loading';
import styles from './UserPicker.module.scss';
import useTranslation from '../../../../../Hooks/useTranslation';
import { getCanAddFamilyMember } from '../../../../User/Services/UserFamily.service';
import { useAppContext } from '../../../../../Services/AppContext';

interface Props {
	selectedUserId?: string;
	linkFamilyMemberSelect?: string;
	onSelect?: (userId: string) => void;
	label: string;
	isInvalid?: boolean;
	showNote?: boolean;
}

const UserPicker: React.FC<Props> = ({
	selectedUserId: value,
	linkFamilyMemberSelect,
	onSelect,
	label,
	isInvalid = false,
	showNote = false,
}) => {
	const [users, setUsers] = useState<UserItem[] | undefined>(undefined);
	const [getUsers] = useLazyFamilyMembersAvailableForCaseCreateQuery();
	const { t } = useTranslation();
	const [isLoading, setIsLoading] = useState(false);
	const { userData, isKdar } = useAppContext();
	const [canAddMember, setCanAddMember] = useState(false);
	const [selectedUserId, setSelectedUserId] = useState(value);

	const onUserSelect = (userId: string) => {
		setSelectedUserId(userId);
	};

	const onSelectChange = (selectValue: SelectValue) => {
		if (typeof selectValue.value === 'string') {
			onUserSelect(selectValue.value);
		}
	};

	const preselectUser = async (userId: string) => {
		if (linkFamilyMemberSelect) {
			await netteApiClient.get(
				linkFamilyMemberSelect.replace('__ID__', userId),
			);
		} else {
			onSelect?.(userId);
		}
	};

	useEffectAsync(async () => {
		if (!selectedUserId) return;
		if (selectedUserId == value) return;
		await preselectUser(selectedUserId);
	}, [selectedUserId]);

	useEffectAsync(async () => {
		setCanAddMember(await getCanAddFamilyMember(userData.id));
	}, []);

	useEffectAsync(async () => {
		if (!users) {
			setIsLoading(true);
			const { familyMembersAvailableForCaseCreate: data } =
				await getUsers().unwrap();

			const tmpUsers: UserItem[] = [];

			data?.data?.forEach(familyMember => {
				if (familyMember) {
					tmpUsers.push({
						id: familyMember.id,
						firstName: familyMember.profile?.firstName || '',
						lastName: familyMember.profile?.lastName || '',
						fullName: familyMember.profile?.fullName || '',
						profileImageUrl: familyMember.profileImageUrl || null,
					});
				}
			});

			setUsers(tmpUsers);
			setIsLoading(false);
		}
	}, []);

	const showSelect = (users?.length || 0) > 1;

	const options: SelectValue[] = useMemo(() => {
		const temp: SelectValue[] = [];
		users?.forEach(user => {
			temp.push({
				value: user.id,
				label: user.fullName,
			});
		});
		return temp;
	}, [users]);

	useEffectAsync(async () => {
		if (!isKdar || !users || users.length < 1) return;
		if (selectedUserId && selectedUserId === value) return;
		await preselectUser(users[0].id);
	}, [isKdar, users]);

	if (isLoading || !users) {
		return (
			<div className={styles.wrapper}>
				<div className={styles.loading}>
					<Loading />
				</div>
			</div>
		);
	}

	return (
		<div className={styles.wrapper}>
			<div className={styles.userPicker}>
				{users?.map(user => (
					<UserPickerItem
						key={user.id}
						user={user}
						onClick={onUserSelect}
						isSelected={user.id === selectedUserId}
					></UserPickerItem>
				))}
				{canAddMember && <UserPickerAdd />}
			</div>
			{showSelect && (
				<div className={styles.formCard}>
					<div className={styles.formGroup}>
						<div className={styles.label}>
							<div className={styles.text}>{t(label)}</div>
						</div>
						<div className={styles.select}>
							<Select
								options={options}
								value={options?.find(option => option.value === selectedUserId)}
								onChange={onSelectChange}
								placeholder='component.userPicker.pickPerson'
								error={
									isInvalid
										? {
												type: '',
												message: t('component.userPicker.youMustPickPerson'),
										  }
										: undefined
								}
							/>
						</div>
						{showNote && (
							<div className={styles.label}>
								<div className={styles.text}>
									{t('component.userPicker.note')}
								</div>
							</div>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

export default UserPicker;
